import * as React from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * MultiSelectCombobox - A searchable multi-select dropdown component
 * 
 * Features:
 * - Search/filter within dropdown
 * - Multiple selection with tags/pills display
 * - Keyboard navigation (arrow keys, enter, escape)
 * - Selected items shown as removable pills
 * - Works with large option lists
 * 
 * @param {Object} props
 * @param {Array} props.options - Array of {value, label} objects
 * @param {Array} props.values - Array of selected values
 * @param {function} props.onValuesChange - Callback when values change
 * @param {string} props.placeholder - Placeholder text when no selection
 * @param {string} props.emptyText - Text shown when no results
 * @param {string} props.className - Additional classes for container
 * @param {boolean} props.disabled - Disable the select
 * @param {boolean} props.sortAlphabetically - Sort options A-Z (default true)
 */
export function MultiSelectCombobox({
  options = [],
  values = [],
  onValuesChange,
  placeholder = "Select items...",
  emptyText = "No results found.",
  className,
  disabled = false,
  sortAlphabetically = true,
  ...props
}) {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  const [highlightedIndex, setHighlightedIndex] = React.useState(0);
  const inputRef = React.useRef(null);
  const listRef = React.useRef(null);
  const containerRef = React.useRef(null);

  // Sort options alphabetically by label if enabled
  const sortedOptions = React.useMemo(() => {
    if (!sortAlphabetically) return options;
    return [...options].sort((a, b) =>
      (a.label || "").localeCompare(b.label || "", undefined, { sensitivity: "base" })
    );
  }, [options, sortAlphabetically]);

  // Filter options based on input (exclude already selected)
  const filteredOptions = React.useMemo(() => {
    let filtered = sortedOptions;
    
    if (inputValue.trim()) {
      const search = inputValue.toLowerCase().trim();
      filtered = filtered.filter((opt) =>
        (opt.label || "").toLowerCase().includes(search)
      );
    }
    
    return filtered;
  }, [sortedOptions, inputValue]);

  // Get labels for selected values
  const selectedOptions = React.useMemo(() => {
    return values
      .map(v => options.find(opt => opt.value === v))
      .filter(Boolean);
  }, [options, values]);

  // Reset highlighted index when filtered options change
  React.useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredOptions.length]);

  // Handle click outside to close dropdown
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        setInputValue("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Scroll highlighted option into view
  React.useEffect(() => {
    if (open && listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex];
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedIndex, open]);

  const toggleOption = (optionValue) => {
    const newValues = values.includes(optionValue)
      ? values.filter(v => v !== optionValue)
      : [...values, optionValue];
    onValuesChange(newValues);
  };

  const removeOption = (optionValue, e) => {
    e?.stopPropagation();
    onValuesChange(values.filter(v => v !== optionValue));
  };

  const handleKeyDown = (e) => {
    if (disabled) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!open) {
          setOpen(true);
        } else {
          setHighlightedIndex((prev) =>
            prev < filteredOptions.length - 1 ? prev + 1 : prev
          );
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (open) {
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        }
        break;
      case "Enter":
        e.preventDefault();
        if (open && filteredOptions[highlightedIndex]) {
          toggleOption(filteredOptions[highlightedIndex].value);
        } else if (!open) {
          setOpen(true);
        }
        break;
      case "Escape":
        e.preventDefault();
        setOpen(false);
        setInputValue("");
        break;
      case "Backspace":
        if (!inputValue && values.length > 0) {
          // Remove last selected item when backspace on empty input
          onValuesChange(values.slice(0, -1));
        }
        break;
      default:
        break;
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    if (!open) {
      setOpen(true);
    }
  };

  const handleContainerClick = () => {
    if (!disabled) {
      inputRef.current?.focus();
      setOpen(true);
    }
  };

  return (
    <div ref={containerRef} className={cn("relative", className)} {...props}>
      {/* Selected items + input container */}
      <div
        onClick={handleContainerClick}
        className={cn(
          "flex flex-wrap gap-1.5 items-center min-h-[42px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm cursor-text",
          "ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
          disabled && "cursor-not-allowed opacity-50"
        )}
      >
        {/* Selected items as pills */}
        {selectedOptions.map((opt) => (
          <span
            key={opt.value}
            className="inline-flex items-center gap-1 bg-primary/10 text-primary rounded-full px-2.5 py-0.5 text-xs font-medium"
          >
            {opt.label}
            <button
              type="button"
              onClick={(e) => removeOption(opt.value, e)}
              className="hover:bg-primary/20 rounded-full p-0.5"
              disabled={disabled}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        
        {/* Search input */}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => !disabled && setOpen(true)}
          placeholder={values.length === 0 ? placeholder : ""}
          disabled={disabled}
          className="flex-1 min-w-[80px] bg-transparent outline-none placeholder:text-muted-foreground"
          data-testid="multi-select-input"
        />
        
        {/* Dropdown toggle */}
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180"
          )}
        />
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-lg">
          <div
            ref={listRef}
            className="max-h-60 overflow-y-auto p-1"
            role="listbox"
          >
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                {emptyText}
              </div>
            ) : (
              filteredOptions.map((opt, index) => {
                const isSelected = values.includes(opt.value);
                const isHighlighted = index === highlightedIndex;
                
                return (
                  <div
                    key={opt.value}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => toggleOption(opt.value)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={cn(
                      "flex items-center justify-between px-3 py-2 text-sm rounded-sm cursor-pointer",
                      isHighlighted && "bg-accent",
                      isSelected && "font-medium"
                    )}
                  >
                    <span>{opt.label}</span>
                    {isSelected && (
                      <Check className="h-4 w-4 text-primary shrink-0" />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
